import React, { useState, useEffect } from 'react';
import {
    Drawer, Box, Typography, IconButton, Divider, Stack, Avatar, Chip, CircularProgress, Paper
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EventIcon from '@mui/icons-material/Event';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ScheduleIcon from '@mui/icons-material/Schedule';
import NotesIcon from '@mui/icons-material/Notes';
import PeopleIcon from '@mui/icons-material/People';
import AudioFileIcon from '@mui/icons-material/AudioFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import ErrorIcon from '@mui/icons-material/Error';
import { format, isToday, isYesterday } from 'date-fns';
import { sk } from 'date-fns/locale';
import { usePreferences } from '../contexts/PreferencesContext';
import { getThemeColors } from '../config/theme';
import eventsService from '../services/events.service';

const EventDetailDrawer = ({ event, open, onClose }) => {
    const { isDarkMode } = usePreferences();
    const colors = getThemeColors(isDarkMode);
    const [eventDetails, setEventDetails] = useState(null);
    const [detailsLoading, setDetailsLoading] = useState(false);

    useEffect(() => {
        if (open && event) {
            loadEventDetails();
        } else {
            setEventDetails(null);
        }
    }, [open, event]);

    const loadEventDetails = async () => {
        if (!event || !event.id) return;
        
        setDetailsLoading(true);
        setEventDetails(null);

        try {
            const response = await eventsService.getEventDetails(event.id);
            setEventDetails(response.data);
        } catch (error) {
            console.error("Failed to fetch event details", error);
        } finally {
            setDetailsLoading(false);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'completed':
                return '#10b981';
            case 'cancelled':
                return '#ef4444';
            case 'scheduled':
                return '#3b82f6';
            default:
                return '#6b7280';
        }
    };

    const formatDate = (date) => {
        if (!date) return null;
        const eventDate = new Date(date);
        if (isToday(eventDate)) {
            return `Dnes ${format(eventDate, 'HH:mm')}`;
        }
        if (isYesterday(eventDate)) {
            return `Včera ${format(eventDate, 'HH:mm')}`;
        }
        return format(eventDate, 'd.M.yyyy HH:mm', { locale: sk });
    };

    if (!event) {
        return null;
    }

    const statusColor = getStatusColor(event.status || eventDetails?.status);

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            keepMounted={false}
            disableRestoreFocus={true}
        >
            <Box sx={{ width: { xs: '100vw', sm: 500 }, p: 3, maxHeight: '100vh', overflowY: 'auto' }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ minWidth: 0 }}>
                        <IconButton onClick={onClose}>
                            <ArrowBackIcon />
                        </IconButton>
                        <Avatar sx={{ bgcolor: statusColor, color: '#ffffff', width: 50, height: 50, fontSize: '1.2rem' }}>
                            <EventIcon />
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                            <Typography variant="h5" component="h1" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: colors.textPrimary }}>
                                {eventDetails?.title || event.title || 'Udalosť'}
                            </Typography>
                        </Box>
                    </Stack>
                </Stack>

                <Divider sx={{ my: 2, borderColor: colors.border }} />

                {detailsLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress sx={{ color: colors.info }} />
                    </Box>
                ) : eventDetails ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        {/* Description */}
                        {eventDetails.description && (
                            <Box>
                                <Typography variant="subtitle2" sx={{ color: colors.textTertiary, mb: 1, fontWeight: 600 }}>
                                    Popis
                                </Typography>
                                <Typography variant="body1" sx={{ color: colors.textPrimary, lineHeight: 1.6 }}>
                                    {eventDetails.description}
                                </Typography>
                            </Box>
                        )}

                        {/* Time */}
                        <Box>
                            <Typography variant="subtitle2" sx={{ color: colors.textTertiary, mb: 1, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <ScheduleIcon sx={{ fontSize: '1rem' }} />
                                Čas
                            </Typography>
                            <Typography variant="body1" sx={{ color: colors.textPrimary }}>
                                Začiatok: {formatDate(eventDetails.start_time) || 'Neznámy'}
                            </Typography>
                            {eventDetails.end_time && (
                                <Typography variant="body1" sx={{ color: colors.textPrimary, mt: 0.5 }}>
                                    Koniec: {formatDate(eventDetails.end_time)}
                                </Typography>
                            )}
                        </Box>

                        {/* Location */}
                        {eventDetails.location && (
                            <Box>
                                <Typography variant="subtitle2" sx={{ color: colors.textTertiary, mb: 1, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <LocationOnIcon sx={{ fontSize: '1rem' }} />
                                    Miesto
                                </Typography>
                                <Typography variant="body1" sx={{ color: colors.textPrimary }}>
                                    {eventDetails.location}
                                </Typography>
                            </Box>
                        )}

                        {/* Status */}
                        <Box>
                            <Typography variant="subtitle2" sx={{ color: colors.textTertiary, mb: 1, fontWeight: 600 }}>
                                Status
                            </Typography>
                            <Chip
                                label={eventDetails.status}
                                size="small"
                                sx={{
                                    bgcolor: `${statusColor}20`,
                                    color: statusColor,
                                    border: `1px solid ${statusColor}40`,
                                }}
                            />
                        </Box>

                        {/* Invited Participants */}
                        {eventDetails.invited_participants && eventDetails.invited_participants.length > 0 && (
                            <Box>
                                <Typography variant="subtitle2" sx={{ color: colors.textTertiary, mb: 1, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <PeopleIcon sx={{ fontSize: '1rem' }} />
                                    Pozvaní účastníci
                                </Typography>
                                <Typography variant="body1" sx={{ color: colors.textPrimary }}>
                                    {eventDetails.invited_participants.length} účastník(ov)
                                </Typography>
                            </Box>
                        )}

                        {/* Notes */}
                        {eventDetails.notes && (
                            <>
                                <Divider sx={{ borderColor: colors.border }} />
                                <Box>
                                    <Typography variant="subtitle2" sx={{ color: colors.textTertiary, mb: 1, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <NotesIcon sx={{ fontSize: '1rem' }} />
                                        Poznámky
                                    </Typography>
                                    <Typography variant="body1" sx={{ color: colors.textPrimary, whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                                        {eventDetails.notes}
                                    </Typography>
                                </Box>
                            </>
                        )}

                        {/* Recordings */}
                        {eventDetails.recordings && eventDetails.recordings.length > 0 && (
                            <>
                                <Divider sx={{ borderColor: colors.border }} />
                                <Box>
                                    <Typography variant="subtitle2" sx={{ color: colors.textTertiary, mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <AudioFileIcon sx={{ fontSize: '1rem' }} />
                                        Nahrávky ({eventDetails.recordings.length})
                                    </Typography>
                                    <Stack spacing={2}>
                                        {eventDetails.recordings.map((recording) => (
                                            <Paper
                                                key={recording.id}
                                                sx={{
                                                    p: 2,
                                                    bgcolor: isDarkMode ? 'rgba(255, 255, 255, 0.03)' : colors.backgroundTertiary,
                                                    border: `1px solid ${colors.border}`,
                                                    borderRadius: 2,
                                                }}
                                            >
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                    <AudioFileIcon sx={{ fontSize: '1.2rem', color: colors.info }} />
                                                    <Typography variant="body2" sx={{ fontWeight: 'bold', flex: 1, color: colors.textPrimary }}>
                                                        {recording.file_name}
                                                    </Typography>
                                                    {recording.processing_status === 'completed' && (
                                                        <CheckCircleIcon sx={{ fontSize: '1rem', color: colors.success }} />
                                                    )}
                                                    {recording.processing_status === 'pending' && (
                                                        <HourglassEmptyIcon sx={{ fontSize: '1rem', color: colors.warning }} />
                                                    )}
                                                    {recording.processing_status === 'failed' && (
                                                        <ErrorIcon sx={{ fontSize: '1rem', color: colors.error }} />
                                                    )}
                                                    <Chip
                                                        label={recording.processing_status}
                                                        size="small"
                                                        sx={{
                                                            height: 20,
                                                            fontSize: '0.65rem',
                                                        }}
                                                    />
                                                </Box>
                                                
                                                {recording.summary && (
                                                    <Box sx={{ mt: 1.5 }}>
                                                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, fontSize: '0.875rem', color: colors.textTertiary }}>
                                                            Súhrn
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ color: colors.textSecondary, whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                                                            {recording.summary}
                                                        </Typography>
                                                    </Box>
                                                )}

                                                {recording.top_ideas && Array.isArray(recording.top_ideas) && recording.top_ideas.length > 0 && (
                                                    <Box sx={{ mt: 1.5 }}>
                                                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, fontSize: '0.875rem', color: colors.textTertiary }}>
                                                            Hlavné myšlienky
                                                        </Typography>
                                                        <Stack spacing={0.5}>
                                                            {recording.top_ideas.map((idea, idx) => (
                                                                <Typography key={idx} variant="body2" sx={{ color: colors.textSecondary, pl: 1, borderLeft: `2px solid ${colors.info}` }}>
                                                                    • {idea}
                                                                </Typography>
                                                            ))}
                                                        </Stack>
                                                    </Box>
                                                )}

                                                {recording.risks_and_mistakes && Array.isArray(recording.risks_and_mistakes) && recording.risks_and_mistakes.length > 0 && (
                                                    <Box sx={{ mt: 1.5 }}>
                                                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, fontSize: '0.875rem', color: colors.warning }}>
                                                            Riziká a chyby
                                                        </Typography>
                                                        <Stack spacing={0.5}>
                                                            {recording.risks_and_mistakes.map((risk, idx) => (
                                                                <Typography key={idx} variant="body2" sx={{ color: colors.textSecondary, pl: 1, borderLeft: `2px solid ${colors.warning}` }}>
                                                                    • {risk}
                                                                </Typography>
                                                            ))}
                                                        </Stack>
                                                    </Box>
                                                )}

                                                {recording.tasks && Array.isArray(recording.tasks) && recording.tasks.length > 0 && (
                                                    <Box sx={{ mt: 1.5 }}>
                                                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, fontSize: '0.875rem', color: colors.info }}>
                                                            Úlohy
                                                        </Typography>
                                                        <Stack spacing={0.5}>
                                                            {recording.tasks.map((task, idx) => (
                                                                <Typography key={idx} variant="body2" sx={{ color: colors.textSecondary, pl: 1, borderLeft: `2px solid ${colors.info}` }}>
                                                                    • {task}
                                                                </Typography>
                                                            ))}
                                                        </Stack>
                                                    </Box>
                                                )}

                                                {recording.transcript && (
                                                    <Box sx={{ mt: 1.5 }}>
                                                        <Typography variant="caption" sx={{ color: colors.textTertiary }}>
                                                            Transcript: {recording.transcript.length} znakov
                                                        </Typography>
                                                    </Box>
                                                )}

                                                <Typography variant="caption" sx={{ color: colors.textTertiary, mt: 1, display: 'block' }}>
                                                    Nahrané: {formatDate(recording.created_at)}
                                                </Typography>
                                            </Paper>
                                        ))}
                                    </Stack>
                                </Box>
                            </>
                        )}

                        {/* Metadata */}
                        {eventDetails.metadata && (
                            <>
                                <Divider sx={{ borderColor: colors.border }} />
                                <Box>
                                    <Typography variant="subtitle2" sx={{ color: colors.textTertiary, mb: 1, fontWeight: 600 }}>
                                        Metadata
                                    </Typography>
                                    <Box
                                        sx={{
                                            p: 1.5,
                                            borderRadius: 2,
                                            bgcolor: isDarkMode ? 'rgba(30, 41, 59, 0.8)' : colors.backgroundTertiary,
                                            border: `1px solid ${colors.border}`,
                                        }}
                                    >
                                        <Typography variant="body2" sx={{ color: colors.textSecondary, fontFamily: 'monospace', fontSize: '0.75rem', whiteSpace: 'pre-wrap' }}>
                                            {JSON.stringify(eventDetails.metadata, null, 2)}
                                        </Typography>
                                    </Box>
                                </Box>
                            </>
                        )}

                        {/* Footer Info */}
                        <Divider sx={{ borderColor: colors.border }} />
                        <Box>
                            <Typography variant="caption" sx={{ color: colors.textTertiary }}>
                                Konverzácia: {eventDetails.conversation_name || 'Neznáma'}
                            </Typography>
                            <br />
                            <Typography variant="caption" sx={{ color: colors.textTertiary }}>
                                Vytvorené: {formatDate(eventDetails.created_at)}
                            </Typography>
                            {eventDetails.updated_at && (
                                <>
                                    <br />
                                    <Typography variant="caption" sx={{ color: colors.textTertiary }}>
                                        Aktualizované: {formatDate(eventDetails.updated_at)}
                                    </Typography>
                                </>
                            )}
                        </Box>
                    </Box>
                ) : (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                        <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                            Nepodarilo sa načítať detaily udalosti.
                        </Typography>
                    </Box>
                )}
            </Box>
        </Drawer>
    );
};

export default EventDetailDrawer;
















